/**
 * cart.ts - Cart line items / totals, donation toggle, financial-assistance
 * shortcut, and the step-3 read-only review summary.
 */
import { CONFIG, state } from './config';
import { goToStep } from './stepNavigation';
import { toggleCreditCardPanel, syncPaymentPanelForStep3 } from './paymentToggles';

export function initCartCalculations(): void {
    const donationCheckbox = document.getElementById('AddOns_SupportDonation') as HTMLInputElement | null;
    if (donationCheckbox) {
        donationCheckbox.addEventListener('change', (e: Event) => {
            const target = e.target as HTMLInputElement;
            state.donationAmount = target.checked ? parseFloat(target.value) : 0.00;
            updateCartSummary();
        });
    }

    const grantLink = document.querySelector('.js-trigger-assistance');
    if (grantLink) {
        grantLink.addEventListener('click', (e: Event) => {
            e.preventDefault();
            const assistanceRadio = document.getElementById('Payment_Method_Assistance') as HTMLInputElement | null;
            if (assistanceRadio) {
                assistanceRadio.checked = true;
                state.paymentMethod = 'Assistance';
                void toggleCreditCardPanel('Assistance');
                goToStep(3);
            }
        });
    }
}

export function updateCartSummary(): void {
    const cartContainer = document.getElementById('CartLineItems');
    const totalDisplay = document.getElementById('CartTotalAmount');
    const submitBtn = document.getElementById('BtnSubmitRegistration');

    if (!cartContainer || !totalDisplay) return;

    cartContainer.innerHTML = '';
    let total = 0.00;

    const playerCards = document.querySelectorAll<HTMLElement>('.player-card');

    playerCards.forEach((card, idx) => {
        const firstName = card.querySelector<HTMLInputElement>('.js-first-name')?.value.trim() || '';
        const lastName = card.querySelector<HTMLInputElement>('.js-last-name')?.value.trim() || '';
        const nameDisplay = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : `Player ${idx + 1}`;

        total += CONFIG.baseEntryFee;

        const lineItem = document.createElement('div');
        lineItem.className = 'd-flex justify-content-between text-dark fw-medium';
        lineItem.innerHTML = `
            <span>${nameDisplay} (Entry)</span>
            <span>$${CONFIG.baseEntryFee.toFixed(2)}</span>
        `;
        cartContainer.appendChild(lineItem);
    });

    if (state.donationAmount > 0) {
        total += state.donationAmount;
        const donationItem = document.createElement('div');
        donationItem.className = 'd-flex justify-content-between text-primary';
        donationItem.innerHTML = `
            <span>Youth Support Donation</span>
            <span>+$${state.donationAmount.toFixed(2)}</span>
        `;
        cartContainer.appendChild(donationItem);
    }

    totalDisplay.textContent = `$${total.toFixed(2)}`;
    if (submitBtn) {
        submitBtn.textContent = `Complete Registration ($${total.toFixed(2)})`;
    }
}

export function renderStep3ReviewSummary(): void {
    // Refresh (or create) the PaymentIntent for the current cart total
    // every time step 3 is entered, in case players/donation changed.
    void syncPaymentPanelForStep3();

    const summaryContainer = document.getElementById('ReviewPlayersSummary');
    if (!summaryContainer) return;

    summaryContainer.innerHTML = '';
    const playerCards = document.querySelectorAll<HTMLElement>('.player-card');

    playerCards.forEach((card, idx) => {
        const firstName = card.querySelector<HTMLInputElement>('.js-first-name')?.value.trim() || '';
        const lastName = card.querySelector<HTMLInputElement>('.js-last-name')?.value.trim() || '';
        const uscfId = card.querySelector<HTMLInputElement>('.js-uscf-input')?.value.trim() || 'N/A';
        const rating = card.querySelector<HTMLInputElement>('.js-rating-input')?.value.trim() || 'Unrated';

        const sectionRadio = document.querySelector<HTMLInputElement>(`input[name="Players[${idx}].SelectedSectionId"]:checked`);
        const sectionName = sectionRadio ? sectionRadio.value : 'Open';

        const byeCheckboxes = document.querySelectorAll<HTMLInputElement>(`input[name="Players[${idx}].RequestedByes"]:checked`);
        const byes = Array.from(byeCheckboxes).map(cb => `Rd ${cb.value}`).join(', ') || 'None';

        const reviewCard = `
            <div class="card border p-3 bg-light">
                <div class="d-flex justify-content-between align-items-center">
                    <strong class="text-primary">${firstName} ${lastName}</strong>
                    <span class="badge bg-secondary">${sectionName} Section</span>
                </div>
                <div class="row g-2 mt-2 small text-muted">
                    <div class="col-6">USCF ID: <strong>${uscfId}</strong></div>
                    <div class="col-6">Rating: <strong>${rating}</strong></div>
                    <div class="col-12">Requested Byes: <strong>${byes}</strong></div>
                </div>
            </div>
        `;
        summaryContainer.insertAdjacentHTML('beforeend', reviewCard);
    });
}